/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Button, Checkbox, Chip,
  FormControlLabel, Grid, Link, MenuItem, Radio, RadioGroup, TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { PaidTaskReview, PaidTaskReviewFinding, PaidTaskReviewState } from "../../types/PaidTaskReview";
import { paidTaskReviewQueryKey, respondToPaidTaskReview, reviewPaidTask } from "../../services/paidTaskReviewService";
import { queryKeys } from "../../services/ReactQuery/reactQueryService";
import { apiGet } from "../../services/Api/apiCall";
import { MediaObject } from "../../types/MediaObject";
import DisplayMediaObject from "../MediaObjectSection/DisplayMediaObject";
import ContentBox from "../ContentBox/ContentBox";
import ButtonLoading from "../Button/ButtonLoading";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import casePaidTaskReviewStyles from "./casePaidTaskReviewStyles";

const severityLabels = { HIGH: "Hoch", MEDIUM: "Mittel", INFO: "Hinweis" };
const severityDescriptions = { HIGH: "Erheblicher oder schwerwiegender Mangel", MEDIUM: "Empfohlene Verbesserung", INFO: "Optionale Anregung" };
const severities: PaidTaskReviewFinding["type"][] = ["HIGH", "MEDIUM", "INFO"];
const evidenceLabels = {
  SUPPORTED: "Belegt", PARTIALLY_SUPPORTED: "Teilweise belegt", CONTRADICTED: "Widersprochen",
  NOT_SUPPORTED: "Nicht belegt", NOT_VERIFIABLE: "Nicht prüfbar",
};
const statusLabels = {
  RUNNING: "Vorprüfung läuft", FAILED: "Überarbeitung erforderlich", PASSED: "Vorprüfung bestanden",
  CHALLENGED: "Stellungnahmen werden anwaltlich geprüft", MANUALLY_RELEASED: "Anwaltlich freigegeben", ERROR: "Vorprüfung nicht abgeschlossen",
};
const sourceLabels: Record<string, string> = {
  task: "Aufgabenbeschreibung und Einreichung", get_case_data: "Falldaten", get_case_documents: "Dokumentübersicht",
  get_case_conversations: "Nachrichten", get_case_notes: "Fallnotizen", get_case_emails: "E-Mails",
  caseData: "Falldaten",
  "rule:objection_guidance": "Behördliche Hinweise zum Widerspruch",
  "rule:administrative_court_guidance": "Gerichtliche Hinweise zum Verwaltungsverfahren",
};

function sourceDetails(sourceId: string, documents: PaidTaskReview["documents"], location: string | null = null) {
  const rule = /^rule:(zpo|vwgo|vwvfg)_(\d+[a-z]?)$/.exec(sourceId);
  if (rule) {
    const lawLabels: Record<string, string> = { zpo: "ZPO", vwgo: "VwGO", vwvfg: "VwVfG" };
    return {
      label: `§ ${rule[2]} ${lawLabels[rule[1]]}`, documentId: null,
      href: `https://www.gesetze-im-internet.de/${rule[1]}/__${rule[2]}.html`,
    };
  }
  const documentReference = /^(submission|case_document):([1-9]\d*)$/.exec(sourceId);
  if (documentReference && Number.isSafeInteger(Number(documentReference[2]))) {
    const id = Number(documentReference[2]);
    const document = documents.find((entry) => entry.id === id);
    return {
      label: document?.originalName || (documentReference[1] === "submission" ? "Eingereichtes Dokument" : "Dokument aus der Fallakte"),
      documentId: document || documentReference[1] === "case_document" ? id : null,
      href: null,
    };
  }
  let label = Object.hasOwn(sourceLabels, sourceId) ? sourceLabels[sourceId] : null;
  const fieldLabel = location?.trim();
  // Only display readable field headings; older technical paths cannot be translated reliably.
  if ((sourceId === "get_case_data" || sourceId === "caseData") && fieldLabel && fieldLabel !== label
    && /^\p{Lu}/u.test(fieldLabel)
    && !/[`{}[\]_=]|[\p{L}\p{N}]\.[\p{L}\p{N}]|\p{Ll}\p{Lu}|\b(?:rule|submission|case_document):/u.test(fieldLabel)) {
    label = `${label} – ${fieldLabel}`;
  }
  return {
    label: label || (sourceId.startsWith("rule:") ? "Rechtliche Prüfvorgabe" : "Weitere Prüfgrundlage"),
    documentId: null, href: null,
  };
}

function sourceUrl(location: string | null): string | null {
  const match = location?.match(/https:\/\/[^\s<>"'()]+/);
  if (!match) {
    return null;
  }
  try {
    const url = new URL(match[0].replace(/[.,;]+$/, ""));
    return url.protocol === "https:" && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}

function reviewText(text: string, documents: PaidTaskReview["documents"]): string {
  // Older review results can contain internal references in the model's explanations.
  // Apply this only to explanations, never to verbatim evidence or user statements.
  return text
    .replace(/`?\b(?:rule:[a-z0-9_]+|(?:submission|case_document):\d+)\b`?/g,
      (reference) => sourceDetails(reference.replaceAll("`", ""), documents).label)
    .replace(/`?\b(caseData|get_case_[a-z_]+)(?:\.[\p{L}\p{N}_]+|\[[^\]\r\n]*\])*`?/gu,
      (_reference, source: string) => sourceLabels[source] || "Falldaten");
}

type CasePaidTaskReviewProps = {
  paidTaskId: number;
  state: PaidTaskReviewState;
  reviewSubmitting: boolean;
  onApproved: () => Promise<void>;
};

export default function CasePaidTaskReview({ paidTaskId, state, reviewSubmitting, onApproved }: CasePaidTaskReviewProps) {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [comments, setComments] = useState<Record<number, string>>({});
  const [decisions, setDecisions] = useState<Record<number, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [severity, setSeverity] = useState("all");
  const [documentId, setDocumentId] = useState("all");
  const [preview, setPreview] = useState<{ document: MediaObject; page: number | null } | null>(null);
  const [resolution, setResolution] = useState<{ findingIds: number[]; comment: string; notifyCustomer: string }>({
    findingIds: [], comment: "", notifyCustomer: "",
  });
  const [confirmApproval, setConfirmApproval] = useState(false);
  const retryMutation = useMutation({
    mutationKey: paidTaskReviewQueryKey(paidTaskId),
    mutationFn: reviewPaidTask,
    onMutate: () => setError(""),
    onSuccess: (next) => queryClient.setQueryData(paidTaskReviewQueryKey(paidTaskId), next),
    onError: () => setError("Die Vorprüfung konnte nicht abgeschlossen werden. Bitte beachten Sie den aktuellen Prüfstatus."),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.item("paid_tasks", paidTaskId) });
      await queryClient.invalidateQueries({ queryKey: paidTaskReviewQueryKey(paidTaskId) });
    },
  });
  const current = state.reviews[0] ?? null;

  if (!state.required && state.reviews.length === 0) {
    return null;
  }

  const canChallenge = (finding: PaidTaskReviewFinding) =>
    state.canChallenge && finding.blocking && finding.challenge === null;
  const statements = current?.findings.filter((finding) => selectedIds.includes(finding.id)
    && canChallenge(finding) && comments[finding.id]?.trim()) ?? [];
  const pending = current?.findings.filter((finding) => finding.challenge !== null && finding.decision === null) ?? [];
  const canFinish = state.canDecide && pending.length > 0 && pending.every((finding) => typeof decisions[finding.id] === "boolean");
  const hasChanges = resolution.findingIds.length > 0 || resolution.comment.trim() !== ""
    || current?.findings.some((finding) => finding.blocking && decisions[finding.id] !== true);

  const respond = async (isDecision: boolean, action: "approve" | "return" = "return") => {
    if (!current || isSaving || (isDecision ? !canFinish : statements.length === 0)) {
      return;
    }
    if (isDecision && action === "approve" && (hasChanges || resolution.notifyCustomer === "")) {
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      const response = isDecision
        ? {
          decisions: pending.map((finding) => ({ findingId: finding.id, accepted: decisions[finding.id], comment: comments[finding.id] ?? "" })),
          resolution: { action, findingIds: resolution.findingIds, comment: resolution.comment, notifyCustomer: resolution.notifyCustomer === "yes" },
        }
        : { statements: statements.map((finding) => ({ findingId: finding.id, comment: comments[finding.id].trim() })) };
      const next = await respondToPaidTaskReview(paidTaskId, current.id, response);
      queryClient.setQueryData(paidTaskReviewQueryKey(paidTaskId), next);
      if (isDecision && action === "approve") {
        await onApproved();
      }
      setSelectedIds([]);
      setComments({});
      setDecisions({});
      setResolution({ findingIds: [], comment: "", notifyCustomer: "" });
      await queryClient.invalidateQueries({ queryKey: queryKeys.item("paid_tasks", paidTaskId) });
    } catch {
      setError("Die Stellungnahmen oder Entscheidungen konnten nicht gespeichert werden. Bitte den aktuellen Stand prüfen und erneut versuchen.");
      await queryClient.invalidateQueries({ queryKey: paidTaskReviewQueryKey(paidTaskId) });
    } finally {
      setIsSaving(false);
      setConfirmApproval(false);
    }
  };

  const openDocument = async (id: number, page: number | null) => {
    setError("");
    try {
      const document = await apiGet<MediaObject>("media_objects", id);
      setPreview({ document, page });
    } catch {
      setError("Das Dokument konnte nicht geöffnet werden.");
    }
  };

  const renderFindings = (review: PaidTaskReview, isCurrent: boolean) => {
    const findings = review.findings.filter((finding) => !isCurrent
      || ((severity === "all" || finding.type === severity)
        && (documentId === "all" || String(finding.documentId) === documentId)));
    return (
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="body2">
            Eingereicht von {review.submittedBy.name} am {dayjs(review.submittedAt).format("DD.MM.YYYY HH:mm")}
            {review.completedAt && ` · Geprüft am ${dayjs(review.completedAt).format("DD.MM.YYYY HH:mm")}`}
          </Typography>
          {review.closedAt && <Typography variant="body2">Runde abgeschlossen</Typography>}
        </Grid>
        {review.resolution?.action === "return" && <Grid size={12}><Alert severity="warning">
          <Typography variant="subtitle2">Anwaltliche Änderungswünsche · {review.resolution.author.name}</Typography>
          <Typography>Bitte beachten Sie die Entscheidungen zu Ihren Stellungnahmen und alle zur Korrektur markierten Anmerkungen.</Typography>
          {review.resolution.comment && <Typography sx={{ whiteSpace: "pre-wrap" }}>{review.resolution.comment}</Typography>}
        </Alert></Grid>}
        {review.findings.length === 0 && ["PASSED", "MANUALLY_RELEASED"].includes(review.status) && (
          <Grid size={12}><Typography>Die Vorprüfung hat keine Anmerkungen ergeben.</Typography></Grid>
        )}
        {findings.length === 0 && review.findings.length > 0 && <Grid size={12}><Typography>Keine Anmerkungen für diese Filterauswahl.</Typography></Grid>}
        {findings.map((finding) => {
          const document = review.documents.find((entry) => entry.id === finding.documentId);
          return (
            <Grid size={12} key={finding.id} sx={{ border: 1, borderColor: "divider", p: 2 }}>
              <Grid container spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                {isCurrent && canChallenge(finding) && (
                  <Grid>
                    <FormControlLabel label="Stellungnahme abgeben" control={
                      <Checkbox checked={selectedIds.includes(finding.id)} disabled={isSaving} onChange={(event) => {
                        setSelectedIds(event.target.checked
                          ? [...selectedIds, finding.id] : selectedIds.filter((id) => id !== finding.id));
                      }} />
                    } />
                  </Grid>
                )}
                <Grid><Chip size="small" sx={casePaidTaskReviewStyles[finding.type]} label={severityLabels[finding.type]} /></Grid>
                <Grid><Typography variant="h6">{reviewText(finding.title, review.documents)}</Typography></Grid>
              </Grid>
              {review.resolution?.findingIds.includes(finding.id) && <Alert severity="warning" sx={{ mb: 1 }}>Korrektur vom Anwalt angefordert</Alert>}
              {isCurrent && state.canDecide && <FormControlLabel label="Korrektur anfordern" control={
                <Checkbox checked={resolution.findingIds.includes(finding.id)} disabled={isSaving} onChange={(event) => {
                  setResolution({ ...resolution, findingIds: event.target.checked
                    ? [...resolution.findingIds, finding.id] : resolution.findingIds.filter((id) => id !== finding.id) });
                }} />
              } />}
              {document && <Button type="button" onClick={() => openDocument(document.id, finding.page)}>
                {document.originalName || "Eingereichtes Dokument"}{finding.page !== null && ` · Seite ${finding.page}`}
              </Button>}
              {finding.section && <Typography variant="body2">{reviewText(finding.section, review.documents)}</Typography>}
              {finding.originalQuote && <Typography component="blockquote" sx={{ m: 0, my: 1 }}>„{finding.originalQuote}“</Typography>}
              <Typography sx={{ whiteSpace: "pre-wrap" }}>{reviewText(finding.reason, review.documents)}</Typography>
              {finding.evidenceStatus && <Typography variant="body2" sx={{ mt: 1 }}>Beleglage: {evidenceLabels[finding.evidenceStatus]}</Typography>}
              {finding.sources.length > 0 && (
                <Accordion disableGutters sx={{ mt: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>Quellen und Belege ({finding.sources.length})</AccordionSummary>
                  <AccordionDetails>
                    {finding.sources.map((source, index) => {
                      const details = sourceDetails(source.sourceId, review.documents, source.location);
                      const href = details.href ?? (source.sourceId.startsWith("rule:") ? sourceUrl(source.location) : null);
                      let heading = <Typography variant="body2">{details.label}</Typography>;
                      if (details.documentId !== null) {
                        const sourceDocumentId = details.documentId;
                        heading = <Link component="button" type="button" variant="body2" onClick={() => openDocument(sourceDocumentId, null)}>{details.label}</Link>;
                      } else if (href) {
                        heading = <Link href={href} target="_blank" rel="noopener noreferrer" variant="body2">{details.label}</Link>;
                      }
                      return (
                        <Grid key={index} sx={{ mb: 1 }}>
                          {heading}
                          {source.quote && <Typography>„{source.quote}“</Typography>}
                        </Grid>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              )}
              {isCurrent && canChallenge(finding) && selectedIds.includes(finding.id) && (
                <TextField fullWidth multiline minRows={3} label="Stellungnahme" sx={{ mt: 2 }}
                  value={comments[finding.id] ?? ""} disabled={isSaving}
                  onChange={(event) => setComments({ ...comments, [finding.id]: event.target.value })}
                  slotProps={{ htmlInput: { maxLength: 10000 } }} />
              )}
              {finding.challenge && <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Stellungnahme · {finding.challenge.author.name} · {dayjs(finding.challenge.createdAt).format("DD.MM.YYYY HH:mm")}</Typography>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>{finding.challenge.comment}</Typography>
              </Alert>}
              {isCurrent && state.canDecide && finding.challenge && !finding.decision && (
                <Grid container spacing={1} sx={{ mt: 2 }}>
                  <Grid size={12}>
                    <Typography variant="subtitle2">Entscheidung zur Stellungnahme</Typography>
                    <RadioGroup value={String(decisions[finding.id] ?? "")}
                      onChange={(event) => setDecisions({ ...decisions, [finding.id]: event.target.value === "true" })}>
                      <FormControlLabel value="true" control={<Radio />} disabled={isSaving} label="Stellungnahme akzeptieren" />
                      <FormControlLabel value="false" control={<Radio />} disabled={isSaving} label="Stellungnahme ablehnen" />
                    </RadioGroup>
                  </Grid>
                  <Grid size={12}>
                    <TextField fullWidth multiline minRows={2} label="Abschließender Kommentar (optional)"
                      value={comments[finding.id] ?? ""} disabled={isSaving}
                      onChange={(event) => setComments({ ...comments, [finding.id]: event.target.value })}
                      slotProps={{ htmlInput: { maxLength: 10000 } }} />
                  </Grid>
                </Grid>
              )}
              {finding.decision && <Alert severity={finding.decision.accepted ? "success" : "warning"} sx={{ mt: 1 }}>
                <Typography variant="subtitle2">{finding.decision.accepted ? "Stellungnahme akzeptiert" : "Stellungnahme abgelehnt – Überarbeitung erforderlich"}</Typography>
                <Typography variant="body2">{finding.decision.author.name} · {dayjs(finding.decision.createdAt).format("DD.MM.YYYY HH:mm")}</Typography>
                {finding.decision.comment && <Typography sx={{ whiteSpace: "pre-wrap" }}>{finding.decision.comment}</Typography>}
              </Alert>}
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <ContentBox headline="Vorprüfung" headlineVariant="h2">
      <Grid container spacing={2}>
        {error && <Grid size={12}><Alert severity="error">{error}</Alert></Grid>}
        {!current && <Grid size={12}><Typography>Beim Einreichen werden Hauptdokument und Anlagen geprüft. Anschließend sehen Sie hier die Ergebnisse.</Typography></Grid>}
        {current && <>
          <Grid size={12}><Typography variant="h3">Runde {current.version}: {statusLabels[current.status]}</Typography></Grid>
          {!state.required && <Grid size={12}><Alert severity={current.resolution?.action === "approve" ? "success" : "info"}>
            {current.resolution?.action === "approve"
              ? "Die Aufgabe wurde anwaltlich abgenommen. Eine erneute Abgabe ist nicht erforderlich."
              : "Die Aufgabe wurde an den regulären Abnahmeprozess übergeben. Weitere Änderungen werden dort geprüft; eine erneute Vorprüfung ist nicht erforderlich."}
          </Alert></Grid>}
          {current.status === "RUNNING" && <Grid size={12}><Alert severity="info">Die Vorprüfung kann einige Minuten dauern. Dateien und Einreichung sind bis zum Abschluss gesperrt. Sie können diese Seite später wieder öffnen.</Alert></Grid>}
          {current.status === "CHALLENGED" && <Grid size={12}><Alert severity="info">
            {state.canDecide
              ? "Entscheiden Sie über jede Stellungnahme und schließen Sie anschließend die Prüfung ab. Beachten Sie auch Anmerkungen ohne Stellungnahme, die der Abgabe entgegenstehen."
              : "Bitte warten Sie den Abschluss der anwaltlichen Prüfung ab. Neue Dokumentversionen und weitere Stellungnahmen sind bis dahin gesperrt."}
          </Alert></Grid>}
          {current.status === "ERROR" && <Grid size={12}>
            <Alert severity="error">Die Vorprüfung konnte aus technischen Gründen nicht abgeschlossen werden.
              {!state.required && " Die Aufgabe wurde zur manuellen anwaltlichen Abnahme übergeben."}
            </Alert>
            {state.canSubmit && state.matchesCurrentSubmission && <ButtonLoading
              type="button" variant="outlined" sx={{ mt: 2 }} isLoading={retryMutation.isPending || reviewSubmitting}
              disabled={state.locked || retryMutation.isPending || reviewSubmitting}
              onClick={() => retryMutation.mutate(paidTaskId)}>Vorprüfung wiederholen</ButtonLoading>}
          </Grid>}
          {state.required && current.status === "FAILED" && state.matchesCurrentSubmission && <Grid size={12}><Alert severity="warning">
            {current.closedAt
              ? "Bitte bearbeiten Sie die anwaltliche Rückmeldung, laden Sie eine überarbeitete Version hoch und reichen Sie diese erneut ein."
              : `Noch zu klärende Anmerkungen vor der Abgabe: ${current.findings.filter((finding) => finding.blocking).length}. Laden Sie eine überarbeitete Version hoch oder nehmen Sie zu einzelnen Anmerkungen begründet Stellung.`}
          </Alert></Grid>}
          {!state.matchesCurrentSubmission && <Grid size={12}><Alert severity="info">Die Dateien oder Aufgabendaten wurden geändert. Diese Ergebnisse gehören zur vorherigen Einreichung.
            {state.required && " Beim erneuten Einreichen startet eine neue Vorprüfung."}
          </Alert></Grid>}
          <Grid size={12} container spacing={1}>
            {severities.map((type) => <Grid key={type}>
              <Chip size="small" sx={casePaidTaskReviewStyles[type]} label={`${severityLabels[type]}: ${severityDescriptions[type]}`} />
            </Grid>)}
            <Grid size={12}><Typography variant="body2">Nur offene Anmerkungen mit „Hoch“ verhindern das Bestehen der Vorprüfung und die automatische Übergabe zur Abnahme.</Typography></Grid>
          </Grid>
          {current.findings.length > 0 && <Grid size={12} container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}><TextField select fullWidth label="Schweregrad" value={severity} onChange={(event) => setSeverity(event.target.value)}>
              <MenuItem value="all">Alle Schweregrade</MenuItem>
              {Object.entries(severityLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField select fullWidth label="Dokument" value={documentId} onChange={(event) => setDocumentId(event.target.value)}>
              <MenuItem value="all">Alle Dokumente</MenuItem><MenuItem value="null">Gesamte Aufgabe</MenuItem>
              {current.documents.map((document) => <MenuItem key={document.id} value={String(document.id)}>{document.originalName || "Eingereichtes Dokument"}</MenuItem>)}
            </TextField></Grid>
          </Grid>}
          <Grid size={12}>{renderFindings(current, true)}</Grid>
          {state.canChallenge && current.findings.some(canChallenge) && <Grid size={12}>
            <Typography sx={{ mb: 1 }}>
              Stellungnahmen zur Einreichung: {statements.length}. Es werden nur Stellungnahmen zu ausgewählten Anmerkungen eingereicht, für die eine Begründung ausgefüllt wurde.
              Danach müssen Sie die anwaltliche Prüfung abwarten und können in dieser Runde keine weiteren Stellungnahmen einreichen.
            </Typography>
            <ButtonLoading type="button" variant="contained" isLoading={isSaving}
              disabled={isSaving || statements.length === 0} onClick={() => respond(false)}>Stellungnahme einreichen</ButtonLoading>
          </Grid>}
          {state.canDecide && <Grid size={12} container spacing={2}>
            <Grid size={12}><Typography>
              Noch ausstehende Entscheidungen zu Stellungnahmen: {pending.filter((finding) => typeof decisions[finding.id] !== "boolean").length}.
              Entscheiden Sie auch über weitere notwendige Korrekturen. Ohne offene Anmerkungen oder Änderungswünsche können Sie diese Fassung direkt abnehmen.
            </Typography></Grid>
            <Grid size={12}><TextField fullWidth multiline minRows={3} label="Weitere Änderungswünsche (optional)"
              value={resolution.comment} disabled={isSaving}
              onChange={(event) => setResolution({ ...resolution, comment: event.target.value })}
              slotProps={{ htmlInput: { maxLength: 10000 } }} /></Grid>
            {canFinish && !hasChanges && <Grid size={12}><TextField select fullWidth label="Mandantschaft informieren?" value={resolution.notifyCustomer}
              disabled={isSaving} onChange={(event) => setResolution({ ...resolution, notifyCustomer: event.target.value })}>
              <MenuItem value="yes">Ja</MenuItem><MenuItem value="no">Nein</MenuItem>
            </TextField></Grid>}
            <Grid size={{ xs: 12, sm: 6 }}><ButtonLoading type="button" variant="contained" isLoading={isSaving}
              disabled={isSaving || !canFinish || !hasChanges} onClick={() => respond(true)}>Mit Änderungswünschen zurückgeben</ButtonLoading></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><ButtonLoading type="button" variant="contained" isLoading={isSaving}
              disabled={isSaving || !canFinish || !!hasChanges || resolution.notifyCustomer === ""}
              onClick={() => setConfirmApproval(true)}>Aufgabe abnehmen</ButtonLoading></Grid>
          </Grid>}
        </>}
        {state.reviews.slice(1).map((review) => <Grid size={12} key={review.id}>
          <Accordion><AccordionSummary expandIcon={<ExpandMoreIcon />}>Runde {review.version} · {statusLabels[review.status]} · {dayjs(review.submittedAt).format("DD.MM.YYYY HH:mm")}</AccordionSummary>
            <AccordionDetails>{renderFindings(review, false)}</AccordionDetails>
          </Accordion>
        </Grid>)}
      </Grid>
      <DisplayMediaObject mediaObject={preview?.document ?? null} initialPage={preview?.page ?? null} closeDialog={() => setPreview(null)} />
      <LegalbirdIoModal open={confirmApproval} handleClose={() => setConfirmApproval(false)} title="Aufgabe abnehmen?"
        submitButton={<ButtonLoading variant="contained" isLoading={isSaving} onClick={() => respond(true, "approve")}>Aufgabe abnehmen</ButtonLoading>}>
        Möchten Sie diese Fassung abnehmen und zur Vergütung freigeben? Es werden alle Dateien und Informationen an den Fall übertragen. Ein Wieder-Öffnen der Aufgabe ist nicht möglich.
      </LegalbirdIoModal>
    </ContentBox>
  );
}
